import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { CalendarClock, Inbox, Phone, Send, StickyNote, Users } from 'lucide-react';
import type { CrmActivity, CrmActivityType } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useCrmApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { fmtDate } from './deals/dealMath';
import type { FocusRequest } from './deals/inlineEditing';

export type ComposerType = Extract<CrmActivityType, 'note' | 'call' | 'meeting' | 'email_out' | 'email_in'>;

const TYPES: ReadonlyArray<{ type: ComposerType; icon: typeof StickyNote }> = [
  { type: 'note', icon: StickyNote },
  { type: 'call', icon: Phone },
  { type: 'meeting', icon: Users },
  { type: 'email_out', icon: Send },
  { type: 'email_in', icon: Inbox },
];

/** `composer:call` etc. — the focus-request targets this box answers to. */
export type ComposerFocusTarget = `composer:${ComposerType}`;
const COMPOSER_TARGET_PREFIX = 'composer:';

function composerTypeOf(request: FocusRequest | null | undefined): ComposerType | null {
  if (!request?.target.startsWith(COMPOSER_TARGET_PREFIX)) return null;
  const type = request.target.slice(COMPOSER_TARGET_PREFIX.length);
  return TYPES.some((entry) => entry.type === type) ? (type as ComposerType) : null;
}

interface Props {
  nip: string;
  dealId: string;
  accountId: string;
  contactId?: string | null;
  /** Primary contact's phone — shows a `tel:` link next to the "Rozmowa" toggle. */
  contactPhone?: string | null;
  /** Shown as the author on the optimistic row until the refetch replaces it. */
  authorName: string;
  /** Switch type and focus the box when the target is `composer:<type>` (next-step card, tel: click). */
  focusRequest?: FocusRequest | null;
  /** Prepend the temporary row (id starts with `tmp-`). */
  onOptimistic: (activity: CrmActivity) => void;
  /** Persisted — refetch the list; receives the chosen occurredAt. */
  onSaved: (occurredAt: string) => void | Promise<void>;
  /** Drop the temporary row after a failed POST. */
  onFailed: (tempId: string) => void;
  /** Unsaved text present — the sheet uses it to arm the double-Esc guard. */
  onDirtyChange?: (dirty: boolean) => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Shared "log an activity" box (deal sheet today, account page later). The
 * row appears immediately and the textarea keeps focus: the POST returns only
 * `{ok}`, so the caller refetches to swap the temp row for the real one.
 */
export function ActivityComposer({
  nip,
  dealId,
  accountId,
  contactId,
  contactPhone,
  authorName,
  focusRequest,
  onOptimistic,
  onSaved,
  onFailed,
  onDirtyChange,
}: Props) {
  const { createActivity } = useCrmApi();
  const [type, setType] = useState<ComposerType>('note');
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  /** null = "now" (resolved at submit time); a Date when back-dated. */
  const [occurredAt, setOccurredAt] = useState<Date | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    onDirtyChange?.(body.trim() !== '' || subject.trim() !== '');
  }, [body, subject, onDirtyChange]);

  useEffect(() => {
    const requested = composerTypeOf(focusRequest);
    if (!requested) return;
    setType(requested);
    textareaRef.current?.focus();
  }, [focusRequest]);

  const reset = () => {
    setBody('');
    setSubject('');
    setOccurredAt(null);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text || saving) return;
    const when = (occurredAt ?? new Date()).toISOString();
    const tempId = `tmp-${Date.now()}`;
    const trimmedSubject = type === 'note' ? null : subject.trim() || null;
    onOptimistic({
      id: tempId,
      type,
      subject: trimmedSubject,
      body: text,
      payload: null,
      occurredAt: when,
      authorUserId: null,
      authorDisplayName: authorName,
      accountId,
      dealId,
      contactId: contactId ?? null,
      leadId: null,
    });
    const snapshot = { body, subject, occurredAt };
    reset();
    setSaving(true);
    try {
      await createActivity(nip, {
        type,
        body: text,
        subject: trimmedSubject,
        // Only send an explicit time when back-dated; the server stamps "now" otherwise.
        ...(occurredAt ? { occurredAt: when } : {}),
        dealId,
        accountId,
        contactId: contactId ?? null,
      });
      await onSaved(when);
    } catch {
      onFailed(tempId);
      setBody(snapshot.body);
      setSubject(snapshot.subject);
      setOccurredAt(snapshot.occurredAt);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
      // Focus stays in the box — the rep is mid-flow, not done.
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void submit();
    }
  };

  const dateLabel = occurredAt
    ? fmtDate(occurredAt, true)
    : t('crm.deals.composer.today', { time: dayjs().format('HH:mm') });

  const setDatePart = (date: Date | undefined) => {
    if (!date) return;
    const base = dayjs(occurredAt ?? new Date());
    setOccurredAt(dayjs(date).hour(base.hour()).minute(base.minute()).second(0).toDate());
  };

  const setTimePart = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    setOccurredAt(dayjs(occurredAt ?? new Date()).hour(h).minute(m).second(0).toDate());
  };

  return (
    <div className="space-y-2" onKeyDown={handleKeyDown}>
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={type}
          onValueChange={(value) => value && setType(value as ComposerType)}
        >
          {TYPES.map(({ type: key, icon: Icon }) => (
            <ToggleGroupItem key={key} value={key} aria-label={t(`crm.activityType.${key}`)} className="text-xs">
              <Icon className="size-3.5" />
              <span className="hidden lg:inline">{t(`crm.activityType.${key}`)}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {contactPhone && type === 'call' ? (
          <Button asChild variant="link" size="xs" className="px-1">
            <a href={`tel:${contactPhone}`}>
              <Phone className="size-3" />
              {t('crm.deals.composer.call')}
            </a>
          </Button>
        ) : null}
      </div>

      <Textarea
        ref={textareaRef}
        rows={2}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={t(`crm.deals.composer.placeholder.${type}`)}
        className="min-h-14 text-sm"
      />

      <div className="flex flex-wrap items-center gap-2">
        {type !== 'note' ? (
          <>
            <Input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder={t('crm.deals.composer.subject')}
              className="h-8 max-w-56 text-sm"
            />
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className={cn('h-8 text-xs', occurredAt && 'font-medium')}>
                  <CalendarClock className="size-3.5" />
                  {dateLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="start">
                <div className="flex flex-wrap gap-1 px-1 pb-1">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => setOccurredAt(dayjs().subtract(1, 'day').toDate())}
                  >
                    {t('crm.deals.composer.yesterday')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => setOccurredAt(dayjs(occurredAt ?? new Date()).subtract(1, 'hour').toDate())}
                  >
                    {t('crm.deals.composer.minusHour')}
                  </Button>
                  <input
                    type="time"
                    aria-label={t('crm.deals.composer.time')}
                    className="bg-background ml-auto h-6 rounded-md border px-1 text-xs"
                    value={dayjs(occurredAt ?? new Date()).format('HH:mm')}
                    onChange={(event) => setTimePart(event.target.value)}
                  />
                </div>
                <Calendar
                  mode="single"
                  selected={occurredAt ?? undefined}
                  defaultMonth={occurredAt ?? undefined}
                  disabled={(date) => date > new Date()}
                  onSelect={(date) => {
                    setDatePart(date);
                    setDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </>
        ) : null}
        <Button
          size="sm"
          className="ml-auto h-8"
          onClick={() => void submit()}
          disabled={saving || body.trim() === ''}
        >
          {t('crm.deals.composer.save')}
          <kbd className="bg-primary-foreground/20 ml-1 rounded px-1 font-mono text-[10px]">
            {isMac ? '⌘↵' : 'Ctrl↵'}
          </kbd>
        </Button>
      </div>
    </div>
  );
}
